import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, LOCALE_ID, OnDestroy, OnInit, ViewChildren, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MdbPopoverModule } from 'mdb-angular-ui-kit/popover';
import { AlertMessage, AlertService, AlertType } from '../../services/alert.service';

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  imports: [MdbPopoverModule, MatIconModule, TranslateModule],
})
export class AlertComponent implements OnInit, OnDestroy {
  private alertService = inject(AlertService);
  private eRef = inject(ElementRef);
  locale = inject(LOCALE_ID);

  @ViewChildren('alert') alert: ElementRef;
  private subscription: Subscription;
  message: string;
  type: string;
  detail: string;
  supportDetail: string;

  timeoutId: ReturnType<typeof setTimeout>;
  alertMessage: AlertMessage;
  showMailSupport = false;
  supportText = '';
  supportEmailLink = '';

  ngOnInit() {
    this.subscription = this.alertService.getAlert().subscribe((alert) => {
      if (!alert) {
        return;
      }

      if (alert.type === AlertType.ACCOUNT_SUSPENDED) {
        //special case for account suspended message
        this.showMailSupport = true;
        this.type = 'danger';
        this.message = 'Votre compte est suspendu.';
        this.detail = null;
        this.supportText = 'Contactez-nous pour le réactiver - support@eemo.fr';
        this.supportEmailLink = this.getAccountSuspendedMailToLink();
      } else if (alert.type === AlertType.CONNECTION_PROBLEM) {
        //special case for multiple invalid credentials
        this.showMailSupport = true;
        this.type = 'danger';
        this.message = 'Vous avez des problèmes pour vous connecter ?';
        this.detail = null;
        this.supportText = 'Contactez-nous, nous allons vous aider - support@eemo.fr';
        this.supportEmailLink = this.getConnectionProblemMailToLink();
      } else if (alert.type === AlertType.PASSWORD_LOST_SUCCESS) {
        this.type = 'success';
        this.message =
          'Un nouveau mot de passe vous a été envoyé sur votre adresse email. Pensez à vérifier votre dossier "indésirables".';
        this.detail = null;
        this.showMailSupport = true;
        this.supportText = 'Si vous ne le recevez pas, contactez-nous, nous allons vous aider - support@eemo.fr';
        this.supportEmailLink = this.getPasswordLostProblemMailToLink();
      } else {
        // generic errors
        this.showMailSupport = false;
        this.alertMessage = alert;
        this.message = alert.text;
        this.detail = null;
        switch (alert.type) {
          case AlertType.SUCCESS:
            this.type = 'success';
            break;
          case AlertType.WARNING:
            this.type = 'warning';
            break;
          case AlertType.ERROR:
            this.type = 'danger';
            break;
          case AlertType.BAD_REQUEST:
            this.showMailSupport = true;
            this.type = 'warning';
            this.detail = this.message ? this.message : '';
            this.message = 'Oups... Il y a un problème.';
            if (alert.apiError) {
              if (this.detail.length > 0) {
                this.detail += ': ';
              }
              this.detail += this.getApiErrorDetails(alert.apiError);
              this.supportDetail = JSON.stringify(alert.apiError);
            }
            break;
          case AlertType.SERVER_ERROR:
            if (alert.apiError && alert.apiError.status === 0) {
              this.showMailSupport = false;
              this.type = 'danger';
              this.message = 'La connexion a été interrompue. Veuillez vérifier votre connexion Internet.';
            } else if (alert.apiError && alert.apiError.status === 502) {
              this.showMailSupport = true;
              this.type = 'danger';
              this.message = 'Une erreur de connexion est survenue. Veuillez réessayer dans quelques minutes.';
              this.detail = '';
              this.supportDetail = JSON.stringify(alert.apiError);
            } else {
              this.showMailSupport = true;
              this.type = 'danger';
              this.detail = this.message ? this.message : '';
              this.message = 'Aie... Une erreur est survenue.';
              if (alert.apiError) {
                if (this.detail.length > 0) {
                  this.detail += ': ';
                }
                this.detail += this.getApiErrorDetails(alert.apiError);
                this.supportDetail = JSON.stringify(alert.apiError);
              }
            }
            break;
        }
        this.timeoutId = setTimeout(
          () => this.closeAlert(),
          (alert.type === AlertType.SUCCESS
            ? environment.alertMessageSuccessHideTimeout
            : environment.alertMessageErrorHideTimeout) * 1000,
        );
        this.supportText = "Signaler l'incident";
        this.supportEmailLink = this.getErrorMailToLink();
      }
    });
  }

  getApiErrorDetails(apiError: HttpErrorResponse): string {
    return apiError.error?.details
      ? apiError.error.details
      : apiError.error?.message
        ? apiError.error.message
        : apiError.statusText;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  closeAlert() {
    this.message = null;
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeAlert();
      clearTimeout(this.timeoutId);
    }
  }
  getAccountSuspendedMailToLink(): string {
    return "mailto:support@eemo.fr?subject=Demande de réactivation d'un compte suspendu&body=Je souhaite réactiver mon compte EEMO. Merci de me recontacter.";
  }
  getConnectionProblemMailToLink(): string {
    return "mailto:support@eemo.fr?subject=Je n'arrive pas à me connecter&body=Je n'arrive pas à me connecter à mon compte EEMO. Merci de me recontacter.";
  }

  getPasswordLostProblemMailToLink(): string {
    return "mailto:support@eemo.fr?subject=Je ne peux pas réinitialiser mon mot de passe&body=Je n'ai pas reçu le mail de réininitialisation du mot de passe de mon compte EEMO. Merci de me recontacter.";
  }

  getErrorMailToLink(): string {
    let link =
      "mailto:support@eemo.fr?subject=Erreur de l'application&body=Type: " +
      this.alertMessage.type +
      '%0D%0ADate: ' +
      formatDate(Date.now(), 'M/d/yy HH:mm:ss z', this.locale) +
      '%0D%0ADetails: ' +
      this.detail +
      '---' +
      this.supportDetail;
    if (this.alertMessage.apiError) {
      link += '%0D%0AApi: ' + this.alertMessage.apiError.url + '%0D%0AHttpCode: ' + this.alertMessage.apiError.status;
    }

    return link;
  }
}
